import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Pencil, Plus, Trash2, Undo2, Upload } from "lucide-react";
import { from, rpc, unwrap, useAction, useHasRole, useOrg } from "@vixio/data";
import {
  Button, Callout, Checkbox, ConfirmDialog, DataTable, Dialog, NumberInput, PageHeader, Panel, Select, StatusFromMap, Tabs, TextArea, TextInput,
} from "@vixio/ui";
import { TESTIMONIAL_STATUS, type Faq, type Media, type Menu, type MenuItem, type Redirect, type SiteSetting, type Testimonial } from "./api";
import { MediaThumb, MediaUploadDialog, siteMediaPath } from "./media";

// ---------------------------------------------------------------- menus

/** Texto do editor: "Rótulo | /caminho"; subitem começa com "- ". */
export function menuToText(items: MenuItem[]): string {
  const top = items.filter((i) => !i.parent_id).sort((a, b) => a.position - b.position);
  return top
    .flatMap((t) => [
      `${t.label} | ${t.href}`,
      ...items.filter((c) => c.parent_id === t.id).sort((a, b) => a.position - b.position).map((c) => `- ${c.label} | ${c.href}`),
    ])
    .join("\n");
}

export function textToMenu(text: string): { items: Array<{ label: string; href: string; children: Array<{ label: string; href: string }> }>; error: string | null } {
  const items: Array<{ label: string; href: string; children: Array<{ label: string; href: string }> }> = [];
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  for (const [n, raw] of lines.entries()) {
    const child = raw.startsWith("-");
    const [label, href] = raw.replace(/^-\s*/, "").split("|").map((s) => s.trim());
    if (!label || !href) return { items, error: `Linha ${n + 1}: use "Rótulo | /caminho".` };
    if (!/^(\/\S*|https:\/\/\S+|mailto:\S+|tel:\+?\d{8,15})$/.test(href)) return { items, error: `Linha ${n + 1}: caminho inválido (${href}).` };
    if (child) {
      const parent = items[items.length - 1];
      if (!parent) return { items, error: `Linha ${n + 1}: subitem sem item acima.` };
      parent.children.push({ label, href });
    } else items.push({ label, href, children: [] });
  }
  return { items, error: null };
}

function MenusTab() {
  const org = useOrg();
  const canEdit = useHasRole("manager");
  const menus = useQuery({ queryKey: ["content", "menus", org.id], queryFn: () => unwrap<Menu[]>(from("content", "menus").select("*").eq("org_id", org.id).order("key")) });
  const items = useQuery({ queryKey: ["content", "menu-items", org.id], queryFn: () => unwrap<MenuItem[]>(from("content", "menu_items").select("*").eq("org_id", org.id)) });
  const [form, setForm] = useState<{ key: string; title: string; text: string } | null>(null);
  const parsed = useMemo(() => (form ? textToMenu(form.text) : null), [form]);
  const save = useAction(() => rpc("content", "set_menu", { p_org: org.id, p_key: form!.key, p_title: form!.title, p_items: parsed!.items }), {
    invalidate: [["content"]],
    success: "Menu salvo",
    onSuccess: () => setForm(null),
  });
  return (
    <div className="vx-stack" style={{ paddingTop: 12 }}>
      {canEdit ? (
        <div>
          <Button size="sm" icon={<Plus />} onClick={() => setForm({ key: "", title: "", text: "" })}>Menu</Button>
        </div>
      ) : null}
      <div className="vx-grid vx-grid--2">
        {(menus.data ?? []).map((m) => {
          const mine = (items.data ?? []).filter((i) => i.menu_id === m.id);
          return (
            <Panel key={m.id} title={`${m.title} (${m.key})`} actions={canEdit ? <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setForm({ key: m.key, title: m.title, text: menuToText(mine) })}>Editar</Button> : null}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {mine.filter((i) => !i.parent_id).sort((a, b) => a.position - b.position).map((t) => (
                  <li key={t.id}>
                    {t.label} <span className="mono text-2">{t.href}</span>
                    {mine.some((c) => c.parent_id === t.id) ? (
                      <ul style={{ paddingLeft: 18 }}>
                        {mine.filter((c) => c.parent_id === t.id).sort((a, b) => a.position - b.position).map((c) => (
                          <li key={c.id}>{c.label} <span className="mono text-2">{c.href}</span></li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Panel>
          );
        })}
      </div>
      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)} title="Menu" onSubmit={() => save.mutate(undefined)}
        footer={<Button type="submit" variant="primary" loading={save.isPending} disabled={!form?.key || !form.title || Boolean(parsed?.error)}>Salvar</Button>}>
        {form ? (
          <div className="vx-stack">
            <div className="vx-grid vx-grid--2">
              <TextInput label="Chave" hint="Ex.: principal, rodape" required value={form.key} onChange={(v) => setForm({ ...form, key: v.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} />
              <TextInput label="Título" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            </div>
            <TextArea label="Itens" hint='Um por linha: "Rótulo | /caminho". Subitem começa com "- ". A ordem das linhas é a ordem do menu.' value={form.text} onChange={(v) => setForm({ ...form, text: v })} rows={10} error={parsed?.error ?? undefined} />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- redirecionamentos

function RedirectsTab() {
  const org = useOrg();
  const canEdit = useHasRole("manager");
  const rows = useQuery({ queryKey: ["content", "redirects", org.id], queryFn: () => unwrap<Redirect[]>(from("content", "redirects").select("*").eq("org_id", org.id).order("from_path")) });
  const [form, setForm] = useState<{ from: string; to: string; code: string; active: boolean; note: string } | null>(null);
  const save = useAction(
    () => rpc("content", "upsert_redirect", { p_org: org.id, p_from_path: form!.from.trim(), p_to_path: form!.to.trim(), p_status_code: Number(form!.code), p_active: form!.active, p_note: form!.note || null }),
    { invalidate: [["content"]], success: "Redirecionamento salvo", onSuccess: () => setForm(null) },
  );
  const remove = useAction((id: string) => rpc("content", "delete_redirect", { p_id: id }), { invalidate: [["content"]], success: "Redirecionamento removido" });
  return (
    <div style={{ paddingTop: 12 }}>
      <DataTable<Redirect>
        caption="Redirecionamentos"
        data={rows.data ?? []}
        rowKey={(r) => r.id}
        searchKeys={["from_path", "to_path"]}
        searchPlaceholder="Buscar endereço"
        exportName="redirecionamentos"
        onRowClick={canEdit ? (r) => setForm({ from: r.from_path, to: r.to_path, code: String(r.status_code), active: r.active, note: r.note ?? "" }) : undefined}
        toolbar={canEdit ? <Button size="sm" icon={<Plus />} onClick={() => setForm({ from: "", to: "", code: "301", active: true, note: "" })}>Redirecionamento</Button> : undefined}
        columns={[
          { key: "from_path", header: "De", cell: (r) => <span className="mono">{r.from_path}</span> },
          { key: "to_path", header: "Para", cell: (r) => <span className="mono">{r.to_path}</span> },
          { key: "status_code", header: "Código", kind: "number" },
          { key: "active", header: "Ativo", cell: (r) => (r.active ? "sim" : "não") },
          { key: "note", header: "Nota", cell: (r) => r.note ?? "" },
          { key: "acoes", header: "", csv: false, cell: (r) => (canEdit ? <Button size="sm" variant="ghost" iconOnly label="Remover" icon={<Trash2 />} onClick={(e) => { e.stopPropagation(); remove.mutate(r.id); }} /> : null) },
        ]}
      />
      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)} title="Redirecionamento" onSubmit={() => save.mutate(undefined)} footer={<Button type="submit" variant="primary" loading={save.isPending}>Salvar</Button>}>
        {form ? (
          <div className="vx-stack">
            <TextInput label="De" hint="Caminho antigo, começando com /" required value={form.from} onChange={(v) => setForm({ ...form, from: v })} className="mono" />
            <TextInput label="Para" hint="Destino final (sem cadeia)" required value={form.to} onChange={(v) => setForm({ ...form, to: v })} className="mono" />
            <Select label="Tipo" value={form.code} onChange={(v) => setForm({ ...form, code: v })} options={[{ value: "301", label: "301 (definitivo)" }, { value: "308", label: "308 (definitivo, mantém o método)" }, { value: "302", label: "302 (temporário)" }]} />
            <TextInput label="Nota" value={form.note} onChange={(v) => setForm({ ...form, note: v })} />
            <Checkbox label="Ativo" checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- FAQ

function FaqTab() {
  const org = useOrg();
  const canEdit = useHasRole("member");
  const rows = useQuery({ queryKey: ["content", "faq", org.id], queryFn: () => unwrap<Faq[]>(from("content", "faq").select("*").eq("org_id", org.id).order("group_key").order("position")) });
  const [form, setForm] = useState<(Omit<Faq, "id"> & { id: string | null }) | null>(null);
  const save = useAction(
    () => rpc("content", "upsert_faq", { p_org: org.id, p_id: form!.id, p_question: form!.question, p_answer_md: form!.answer_md, p_group_key: form!.group_key, p_position: form!.position, p_published: form!.published }),
    { invalidate: [["content"]], success: "Pergunta salva", onSuccess: () => setForm(null) },
  );
  const remove = useAction((id: string) => rpc("content", "delete_faq", { p_id: id }), { invalidate: [["content"]], success: "Pergunta removida" });
  return (
    <div style={{ paddingTop: 12 }}>
      <DataTable<Faq>
        caption="Perguntas frequentes do site"
        data={rows.data ?? []}
        rowKey={(f) => f.id}
        searchKeys={["question", "answer_md"]}
        searchPlaceholder="Buscar pergunta"
        exportName="faq-do-site"
        onRowClick={canEdit ? (f) => setForm({ ...f }) : undefined}
        toolbar={canEdit ? <Button size="sm" icon={<Plus />} onClick={() => setForm({ id: null, group_key: "geral", question: "", answer_md: "", position: 100, published: true })}>Pergunta</Button> : undefined}
        columns={[
          { key: "group_key", header: "Grupo" },
          { key: "position", header: "Ordem", kind: "number" },
          { key: "question", header: "Pergunta" },
          { key: "published", header: "No site", cell: (f) => (f.published ? "sim" : "não") },
          { key: "acoes", header: "", csv: false, cell: (f) => (canEdit ? <Button size="sm" variant="ghost" iconOnly label="Remover" icon={<Trash2 />} onClick={(e) => { e.stopPropagation(); remove.mutate(f.id); }} /> : null) },
        ]}
      />
      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)} title="Pergunta frequente" onSubmit={() => save.mutate(undefined)} footer={<Button type="submit" variant="primary" loading={save.isPending}>Salvar</Button>}>
        {form ? (
          <div className="vx-stack">
            <div className="vx-grid vx-grid--2">
              <TextInput label="Grupo" hint="Ex.: sobre, contato, geral" value={form.group_key} onChange={(v) => setForm({ ...form, group_key: v.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} />
              <NumberInput label="Ordem" value={form.position} min={0} max={9999} onChange={(v) => setForm({ ...form, position: v ?? 100 })} />
            </div>
            <TextInput label="Pergunta" required value={form.question} onChange={(v) => setForm({ ...form, question: v })} />
            <TextArea label="Resposta" required value={form.answer_md} onChange={(v) => setForm({ ...form, answer_md: v })} rows={5} />
            <Checkbox label="Publicada no site" checked={form.published} onChange={(v) => setForm({ ...form, published: v })} />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- depoimentos

function TestimonialsTab() {
  const org = useOrg();
  const canWrite = useHasRole("member");
  const canApprove = useHasRole("manager");
  const rows = useQuery({ queryKey: ["content", "testimonials", org.id], queryFn: () => unwrap<Testimonial[]>(from("content", "testimonials").select("*").eq("org_id", org.id).order("position")) });
  const [form, setForm] = useState<{ id: string | null; name: string; role: string; orgLabel: string; text: string; consent: string } | null>(null);
  const [withdrawing, setWithdrawing] = useState<Testimonial | null>(null);
  const save = useAction(
    () => rpc("content", "upsert_testimonial", { p_org: org.id, p_id: form!.id, p_author_role: form!.role, p_text: form!.text, p_author_name: form!.name || null, p_organization_label: form!.orgLabel || null, p_consent_evidence: form!.consent || null }),
    { invalidate: [["content"]], success: "Depoimento salvo", onSuccess: () => setForm(null) },
  );
  const approve = useAction((id: string) => rpc("content", "approve_testimonial", { p_id: id }), { invalidate: [["content"]], success: "Depoimento aprovado: vai ao site" });
  const withdraw = useAction((id: string) => rpc("content", "withdraw_testimonial", { p_id: id }), { invalidate: [["content"]], success: "Depoimento retirado do site" });
  return (
    <div className="vx-stack" style={{ paddingTop: 12 }}>
      <Callout tone="info">Depoimento só vai ao site aprovado e com a evidência da autorização do autor. Aprovado, o texto não muda.</Callout>
      <DataTable<Testimonial>
        caption="Depoimentos"
        data={rows.data ?? []}
        rowKey={(t) => t.id}
        searchKeys={["text", "author_name", "author_role"]}
        searchPlaceholder="Buscar depoimento"
        exportName="depoimentos"
        onRowClick={canWrite ? (t) => t.status === "pending" && setForm({ id: t.id, name: t.author_name ?? "", role: t.author_role, orgLabel: t.organization_label ?? "", text: t.text, consent: t.consent_evidence ?? "" }) : undefined}
        toolbar={canWrite ? <Button size="sm" icon={<Plus />} onClick={() => setForm({ id: null, name: "", role: "", orgLabel: "", text: "", consent: "" })}>Depoimento</Button> : undefined}
        columns={[
          { key: "text", header: "Depoimento", cell: (t) => <span className="vx-table__primary"><span>“{t.text}”</span><span className="vx-table__sub">{[t.author_name, t.author_role, t.organization_label].filter(Boolean).join(", ")}</span></span> },
          { key: "consent_evidence", header: "Autorização", cell: (t) => t.consent_evidence ?? <span className="text-warn">sem evidência</span> },
          { key: "status", header: "Situação", cell: (t) => <StatusFromMap map={TESTIMONIAL_STATUS} value={t.status} /> },
          {
            key: "acoes",
            header: "",
            csv: false,
            cell: (t) =>
              canApprove && t.status === "pending" ? (
                <Button size="sm" icon={<Check />} disabled={!t.consent_evidence} onClick={(e) => { e.stopPropagation(); approve.mutate(t.id); }}>Aprovar</Button>
              ) : canApprove && t.status === "approved" ? (
                <Button size="sm" variant="danger" icon={<Undo2 />} onClick={(e) => { e.stopPropagation(); setWithdrawing(t); }}>Retirar</Button>
              ) : null,
          },
        ]}
      />
      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)} title="Depoimento" onSubmit={() => save.mutate(undefined)} footer={<Button type="submit" variant="primary" loading={save.isPending}>Salvar</Button>}>
        {form ? (
          <div className="vx-stack">
            <TextArea label="Texto" required value={form.text} onChange={(v) => setForm({ ...form, text: v })} rows={4} maxLength={2000} />
            <div className="vx-grid vx-grid--2">
              <TextInput label="Nome (opcional)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <TextInput label="Cargo" required value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
            </div>
            <TextInput label="Organização (como aparece)" hint="Ex.: Clínica de médio porte, Sudoeste da Bahia" value={form.orgLabel} onChange={(v) => setForm({ ...form, orgLabel: v })} />
            <TextInput label="Evidência da autorização" hint="Ex.: e-mail de 12/09 autorizando o uso" value={form.consent} onChange={(v) => setForm({ ...form, consent: v })} />
          </div>
        ) : null}
      </Dialog>
      <ConfirmDialog open={withdrawing !== null} onOpenChange={(o) => !o && setWithdrawing(null)} title="Retirar depoimento do site" confirmLabel="Retirar" destructive onConfirm={() => withdrawing && withdraw.mutateAsync(withdrawing.id)}>
        <p>O depoimento sai do site e não volta: para usar de novo, registre outro com nova autorização.</p>
      </ConfirmDialog>
    </div>
  );
}

// ---------------------------------------------------------------- mídia

function MediaTab() {
  const org = useOrg();
  const canWrite = useHasRole("member");
  const canDelete = useHasRole("manager");
  const rows = useQuery({ queryKey: ["content", "media", org.id], queryFn: () => unwrap<Media[]>(from("content", "media").select("*").eq("org_id", org.id).order("created_at", { ascending: false })) });
  const [form, setForm] = useState<{ id: string | null; path: string; mime: string; size: number | null; alt: string; caption: string; credit: string } | null>(null);
  const register = useAction(
    () =>
      form!.id
        ? rpc("content", "update_media", { p_media: form!.id, p_alt_text: form!.alt || null, p_caption: form!.caption || null, p_credit: form!.credit || null }).then(() => form!.id!)
        : rpc("content", "register_media", { p_org: org.id, p_path: `${org.id}/${form!.path.replace(/^\/+/, "")}`, p_mime_type: form!.mime, p_size_bytes: form!.size ?? 0, p_alt_text: form!.alt || null, p_caption: form!.caption || null, p_credit: form!.credit || null }),
    { invalidate: [["content"]], success: "Mídia salva", onSuccess: () => setForm(null) },
  );
  const remove = useAction((id: string) => rpc("content", "delete_media", { p_media: id }), { invalidate: [["content"]], success: "Mídia removida" });
  const [uploading, setUploading] = useState(false);
  const copy = useAction((m: Media) => navigator.clipboard.writeText(siteMediaPath(m.path)), { success: "Caminho do site copiado" });
  return (
    <div className="vx-stack" style={{ paddingTop: 12 }}>
      <p className="text-2">
        Imagens e PDFs do site, no armazenamento público (<span className="mono">content-media</span>) e servidos pelo próprio domínio do site em{" "}
        <span className="mono">/media/…</span>. Imagem precisa de texto alternativo; print de manchete, do crédito da fonte.
      </p>
      <DataTable<Media>
        caption="Mídia"
        data={rows.data ?? []}
        rowKey={(m) => m.id}
        searchKeys={["path", "alt_text", "caption"]}
        searchPlaceholder="Buscar arquivo"
        exportName="midia"
        onRowClick={canWrite ? (m) => setForm({ id: m.id, path: m.path, mime: m.mime_type, size: m.size_bytes, alt: m.alt_text ?? "", caption: m.caption ?? "", credit: m.credit ?? "" }) : undefined}
        toolbar={
          canWrite ? (
            <span className="vx-row">
              <Button size="sm" variant="primary" icon={<Upload />} onClick={() => setUploading(true)}>Enviar arquivo</Button>
              <Button size="sm" icon={<Plus />} onClick={() => setForm({ id: null, path: "", mime: "image/png", size: null, alt: "", caption: "", credit: "" })}>Registrar arquivo já no armazenamento</Button>
            </span>
          ) : undefined
        }
        columns={[
          { key: "thumb", header: "", csv: false, cell: (m) => <MediaThumb media={m} size={40} /> },
          { key: "path", header: "Arquivo", cell: (m) => <span className="mono" style={{ overflowWrap: "anywhere" }}>{m.path.split("/").slice(1).join("/")}</span> },
          { key: "site", header: "No site", csv: (m) => siteMediaPath(m.path), cell: (m) => (
            <Button size="sm" variant="ghost" icon={<Copy />} onClick={(e) => { e.stopPropagation(); copy.mutate(m); }} aria-label={`Copiar caminho do site de ${m.path.split("/").pop()}`}>
              Copiar caminho
            </Button>
          ) },
          { key: "mime_type", header: "Tipo" },
          { key: "size_bytes", header: "Tamanho (KB)", align: "right", cell: (m) => <span className="num">{Math.round(m.size_bytes / 1024)}</span> },
          { key: "alt_text", header: "Texto alternativo", cell: (m) => m.alt_text ?? <span className="muted">sem</span> },
          { key: "credit", header: "Crédito", cell: (m) => m.credit ?? "" },
          { key: "acoes", header: "", csv: false, cell: (m) => (canDelete ? <Button size="sm" variant="ghost" iconOnly label="Remover" icon={<Trash2 />} onClick={(e) => { e.stopPropagation(); remove.mutate(m.id); }} /> : null) },
        ]}
      />
      {uploading ? <MediaUploadDialog onClose={() => setUploading(false)} /> : null}
      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? "Mídia" : "Registrar arquivo"} onSubmit={() => register.mutate(undefined)} footer={<Button type="submit" variant="primary" loading={register.isPending}>Salvar</Button>}>
        {form ? (
          <div className="vx-stack">
            {form.id ? (
              <p className="mono text-2" style={{ overflowWrap: "anywhere" }}>{form.path}</p>
            ) : (
              <>
                <TextInput label="Caminho no bucket" hint="Dentro da pasta da organização, ex.: blog/capa-inventario.png" required value={form.path} onChange={(v) => setForm({ ...form, path: v })} />
                <div className="vx-grid vx-grid--2">
                  <Select label="Tipo" value={form.mime} onChange={(v) => setForm({ ...form, mime: v })} options={["image/png", "image/jpeg", "image/webp", "image/svg+xml", "application/pdf", "video/mp4"].map((v) => ({ value: v, label: v }))} />
                  <NumberInput label="Tamanho (bytes)" value={form.size} min={1} onChange={(v) => setForm({ ...form, size: v })} />
                </div>
              </>
            )}
            <TextInput label="Texto alternativo" required={form.mime.startsWith("image/")} value={form.alt} onChange={(v) => setForm({ ...form, alt: v })} />
            <TextInput label="Legenda" value={form.caption} onChange={(v) => setForm({ ...form, caption: v })} />
            <TextInput label="Crédito" value={form.credit} onChange={(v) => setForm({ ...form, credit: v })} />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- configurações

function SettingsTab() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const rows = useQuery({ queryKey: ["content", "settings", org.id], queryFn: () => unwrap<SiteSetting[]>(from("content", "site_settings").select("*").eq("org_id", org.id).order("key")) });
  const [form, setForm] = useState<{ key: string; value: string; category: string; isPublic: boolean; isNew: boolean } | null>(null);
  let valueError: string | null = null;
  let parsedValue: unknown = null;
  if (form) {
    try {
      parsedValue = JSON.parse(form.value);
    } catch {
      parsedValue = form.value;
    }
    if (form.value.trim() === "") valueError = "Informe o valor.";
  }
  const save = useAction(() => rpc("content", "set_setting", { p_org: org.id, p_key: form!.key, p_value: parsedValue, p_category: form!.category, p_is_public: form!.isPublic }), {
    invalidate: [["content"]],
    success: "Configuração salva",
    onSuccess: () => setForm(null),
  });
  return (
    <div style={{ paddingTop: 12 }}>
      <DataTable<SiteSetting>
        caption="Configurações do site"
        data={rows.data ?? []}
        rowKey={(s) => s.id}
        searchKeys={["key", "category"]}
        exportName="configuracoes-do-site"
        onRowClick={isAdmin ? (s) => setForm({ key: s.key, value: typeof s.value === "string" ? s.value : JSON.stringify(s.value), category: s.category, isPublic: s.is_public, isNew: false }) : undefined}
        toolbar={isAdmin ? <Button size="sm" icon={<Plus />} onClick={() => setForm({ key: "", value: "", category: "geral", isPublic: true, isNew: true })}>Configuração</Button> : undefined}
        columns={[
          { key: "key", header: "Chave", cell: (s) => <span className="mono">{s.key}</span> },
          { key: "category", header: "Grupo" },
          { key: "value", header: "Valor", cell: (s) => <span className="mono" style={{ overflowWrap: "anywhere" }}>{typeof s.value === "string" ? s.value : JSON.stringify(s.value)}</span>, csv: (s) => JSON.stringify(s.value) },
          { key: "is_public", header: "Pública", cell: (s) => (s.is_public ? "sim" : "não") },
          { key: "updated_at", header: "Atualizada", kind: "datetime" },
        ]}
      />
      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)} title="Configuração do site" onSubmit={() => save.mutate(undefined)} footer={<Button type="submit" variant="primary" loading={save.isPending} disabled={!form?.key || Boolean(valueError)}>Salvar</Button>}>
        {form ? (
          <div className="vx-stack">
            <TextInput label="Chave" required value={form.key} onChange={(v) => setForm({ ...form, key: v.toLowerCase().replace(/[^a-z0-9_.]/g, "") })} disabled={!form.isNew} className="mono" />
            <TextArea label="Valor" hint="Texto ou JSON." value={form.value} onChange={(v) => setForm({ ...form, value: v })} rows={3} error={valueError ?? undefined} />
            <TextInput label="Grupo" value={form.category} onChange={(v) => setForm({ ...form, category: v.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} />
            <Checkbox label="Pública (o site lê)" checked={form.isPublic} onChange={(v) => setForm({ ...form, isPublic: v })} />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

export function StructurePage() {
  return (
    <div className="vx-content">
      <PageHeader title="Estrutura do site" description="Menus, redirecionamentos 301, perguntas frequentes, depoimentos aprovados, mídia e configurações." />
      <Tabs
        label="Estrutura do site"
        tabs={[
          { value: "menus", label: "Menus", content: <MenusTab /> },
          { value: "redirecionamentos", label: "Redirecionamentos", content: <RedirectsTab /> },
          { value: "faq", label: "Perguntas frequentes", content: <FaqTab /> },
          { value: "depoimentos", label: "Depoimentos", content: <TestimonialsTab /> },
          { value: "midia", label: "Mídia", content: <MediaTab /> },
          { value: "configuracoes", label: "Configurações", content: <SettingsTab /> },
        ]}
      />
    </div>
  );
}
